/**
 * Tasks API клиент — CRUD над /api/chat/tasks/*
 */
import { api } from "@/api/client";

export interface TaskAssignee {
  user_id: string;
  display_name: string | null;
  avatar_filename: string | null;
}

export type TaskStatus = "open" | "in_progress" | "done" | "cancelled";

export interface Task {
  id: number;
  topic_id: number;
  parent_task_id: number | null;
  title: string;
  description: string | null;
  creator_user_id: string;
  creator_display_name: string | null;
  deadline_at: string | null;
  status: TaskStatus;
  completed_at: string | null;
  created_at: string;
  assignees: TaskAssignee[];
  subtask_count: number;
  subtask_done_count: number;
}

export interface TaskListResp {
  topic_id: number;
  tasks: Task[];
}

export const fetchTaskList = (topicId: number, statusFilter?: TaskStatus): Promise<TaskListResp> => {
  const q = statusFilter ? `?status_filter=${statusFilter}` : "";
  return api(`/api/chat/tasks/${topicId}/list${q}`);
};

export const fetchSubtasks = (taskId: number): Promise<Task[]> =>
  api(`/api/chat/tasks/task/${taskId}/subtasks`);

export const createTask = (topicId: number, payload: {
  title: string;
  description?: string;
  deadline_at?: string | null;
  assignee_user_ids?: string[];
  parent_task_id?: number | null;
}): Promise<Task> =>
  api(`/api/chat/tasks/${topicId}/create`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

export const patchTask = (taskId: number, payload: {
  title?: string;
  description?: string;
  deadline_at?: string | null;
  status?: TaskStatus;
  assignee_user_ids?: string[];
}): Promise<Task> =>
  api(`/api/chat/tasks/task/${taskId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

export const deleteTask = (taskId: number): Promise<void> =>
  api(`/api/chat/tasks/task/${taskId}`, { method: "DELETE" });

// ── Comments ──────────────────────────────────────────

export interface TaskComment {
  id: number;
  task_id: number;
  author_user_id: string;
  author_display_name: string | null;
  author_avatar_filename: string | null;
  text: string;
  created_at: string;
}

export const fetchTaskComments = (taskId: number): Promise<TaskComment[]> =>
  api(`/api/chat/tasks/task/${taskId}/comments`);

export const createTaskComment = (taskId: number, text: string): Promise<TaskComment> =>
  api(`/api/chat/tasks/task/${taskId}/comments`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text }),
  });

export const deleteTaskComment = (commentId: number): Promise<void> =>
  api(`/api/chat/tasks/task/comment/${commentId}`, { method: "DELETE" });
